import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { AnimatePresence, motion } from "motion/react";
import { ReactNode, useMemo, useRef } from "react";
import { BracketStageId, useBountyCalculator } from "../../providers/BountyCalculator";
import { generateTeamLogoSrc } from "~/shared/helpers/url/generateTeamLogoSrc";
import classNames from "classnames";

function TeamMenuItem({ children, ...props }: { children: React.ReactNode; onClick?: () => void }) {
  return (
    <MenuItem
      className="flex cursor-pointer items-center gap-x-1 px-1 py-0.5 hover:bg-background-90"
      as={motion.div}
      {...props}
      variants={{
        closed: { x: -16, opacity: 0 },
        open: { x: 0, opacity: 1 },
      }}
      transition={{ opacity: { duration: 0.2 } }}
      onClick={props.onClick}
    >
      {children}
    </MenuItem>
  );
}

export enum TeamSlot {
  A = "A",
  B = "B",
}

export const TeamDropdown = ({
  matchId,
  children,
  teamSlot,
  stageId,
  disabled,
}: {
  matchId: string;
  children: ReactNode;
  teamSlot: TeamSlot;
  stageId: BracketStageId;
  disabled?: boolean;
}) => {
  const menuItemsRef = useRef<HTMLDivElement | null>(null);

  const { initialTeams, currentStandings, stages, setMatchTeamA, setMatchTeamB } = useBountyCalculator();
  const validTeams = useMemo(() => {
    const stage = stages.find((stage) => stage.stageId === stageId);
    if (!stage) return initialTeams;

    // Find the teams that haven't been eliminated in prior stages, or not yet put in a match for current stage.
    const priorStages = stages.slice(0, stages.indexOf(stage));
    const priorStageMatches = priorStages.flatMap((stage) => stage.matches);

    // Find teams that have not been put in a match for this stage
    const teamsInMatches = stage.matches.flatMap((match) => [match.teamA?.id, match.teamB?.id].filter(Boolean));

    // Find the teams that have been eliminated in prior stages
    const eliminatedTeams = priorStageMatches.flatMap(({ loserTeamId }) => loserTeamId);

    return (
      initialTeams
        .filter((team) => {
          return !teamsInMatches.includes(team.id) && !eliminatedTeams.includes(team.id);
        })
        // Sort by bounty highest to lowest, if bounties are equal -- then by their seed highest to lowest
        .sort((a, b) => {
          const standingsA = currentStandings.find(({ id }) => id === a?.id);
          const standingsB = currentStandings.find(({ id }) => id === b?.id);

          const bountyA = standingsA?.bountyValue ?? a?.bountyValue ?? 0;
          const bountyB = standingsB?.bountyValue ?? b?.bountyValue ?? 0;

          const seedA = standingsA?.bountySeed ?? a?.bountySeed ?? 0;
          const seedB = standingsB?.bountySeed ?? b?.bountySeed ?? 0;

          if (bountyB !== bountyA) return bountyB - bountyA;
          return seedA - seedB;
        })
    );
  }, [currentStandings, initialTeams, stageId, stages]);

  // Get all the teams that're in the lower half of the seedings for this stage, that are on matches.
  const swappableTeams = useMemo(() => {
    // All teamB's should be the lower half of the seedings, so let's just show all teamB's from this stage
    const stage = stages.find((stage) => stage.stageId === stageId);
    if (!stage) return [];

    const match = stage.matches.find((match) => match.id === matchId);

    return (
      stage.matches
        .map((match) => match.teamB)
        // Exclude the teamB from the current match
        .filter((team) => team?.id !== match?.teamB?.id)
        .filter(Boolean)
        .sort((a, b) => {
          const standingsA = currentStandings.find(({ id }) => id === a?.id);
          const standingsB = currentStandings.find(({ id }) => id === b?.id);

          const bountyA = standingsA?.bountyValue ?? a?.bountyValue ?? 0;
          const bountyB = standingsB?.bountyValue ?? b?.bountyValue ?? 0;

          const getSeed = (standings: typeof standingsA, team: typeof a) => {
            const bountySeed = standings?.bountySeed;
            return bountySeed === -1 ? (team?.bountySeed ?? 0) : (bountySeed ?? team?.bountySeed ?? 0);
          };

          const seedA = getSeed(standingsA, a);
          const seedB = getSeed(standingsB, b);

          if (bountyB !== bountyA) return bountyB - bountyA;
          return seedA - seedB;
        })
    );
  }, [currentStandings, matchId, stageId, stages]);

  // Format 60000 to $60K using IntlNumberFormat
  const formatBountyValue = (bountyValue: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      notation: "compact",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(bountyValue);
  };

  return (
    <Menu>
      {({ open }) => {
        // Evaluate the distinance from the top left corner of thwe menuItems element to the bottom of the screen, and set the menuItems height to that value
        const bottom = menuItemsRef.current?.getBoundingClientRect().bottom;

        const menuItemsHeight = bottom && typeof window !== "undefined" ? window.innerHeight - bottom : "auto";

        return (
          <>
            {disabled ? (
              <>{children}</>
            ) : (
              <MenuButton className="w-full" disabled={disabled}>
                {children}
              </MenuButton>
            )}
            <div ref={menuItemsRef}></div>
            <AnimatePresence>
              {open && (
                <MenuItems
                  static
                  as={motion.div}
                  variants={{
                    closed: {
                      maxHeight: 0,
                      transition: {
                        delay: 0.075,
                      },
                    },
                    open: {
                      maxHeight: menuItemsHeight,
                      transition: {
                        type: "spring",
                        duration: 0.2,
                        delayChildren: 0.1,
                        staggerChildren: 0.015,
                      },
                    },
                  }}
                  initial="closed"
                  animate="open"
                  exit="closed"
                  className="absolute -top-0 z-toast flex w-full origin-top flex-col overflow-y-auto rounded-small bg-transparent p-px scrollbar-thin"
                >
                  <div
                    className={classNames("rounded-t-small border-2 border-background-85 bg-background-85", {
                      "rounded-b-small": !swappableTeams.length,
                    })}
                  >
                    {validTeams.map((team) => {
                      const standings = currentStandings.find(({ id }) => id === team.id);

                      return (
                        <TeamMenuItem
                          key={team.id}
                          onClick={
                            teamSlot === TeamSlot.A
                              ? () => setMatchTeamA(matchId, team.id)
                              : () => setMatchTeamB(matchId, team.id)
                          }
                        >
                          <img
                            src={generateTeamLogoSrc({ id: team.id, width: 64 })}
                            alt=""
                            className="size-4 object-contain"
                          />
                          <span className="truncate font-style-body-b4">{team.name}</span>
                          <span className="text-neutral font-style-body-b5">
                            {standings?.bountySeed ?? team?.bountySeed ?? "N/A"}
                          </span>
                          <span className="bg-gradient-to-l from-[#FFD807] to-[#FFFADF] bg-clip-text text-transparent font-style-body-b5">
                            {formatBountyValue(standings?.bountyValue ?? team?.bountyValue ?? 0)}
                          </span>
                        </TeamMenuItem>
                      );
                    })}
                  </div>
                  <div className="rounded-small border-2 border-background-100 bg-background-100">
                    {swappableTeams.length > 0 && (
                      <motion.div
                        variants={{
                          closed: { x: -16, opacity: 0 },
                          open: { x: 0, opacity: 1 },
                        }}
                        transition={{ opacity: { duration: 0.2 } }}
                        className="px-1 py-2 text-neutral font-style-body-b5"
                      >
                        Already Selected
                      </motion.div>
                    )}
                    {swappableTeams.map((team) => {
                      if (!team) return null;
                      const standings = currentStandings.find(({ id }) => id === team.id);

                      return (
                        <TeamMenuItem
                          key={team.id}
                          onClick={
                            teamSlot === TeamSlot.A
                              ? () => setMatchTeamA(matchId, team.id)
                              : () => setMatchTeamB(matchId, team.id)
                          }
                        >
                          <img
                            src={generateTeamLogoSrc({ id: team.id, width: 64 })}
                            alt=""
                            className="size-4 object-contain"
                          />
                          <span className="truncate font-style-body-b4">{team.name}</span>
                          <span className="text-neutral font-style-body-b5">{team?.bountySeed ?? "N/A"}</span>
                          <span className="bg-gradient-to-l from-[#FFD807] to-[#FFFADF] bg-clip-text text-transparent font-style-body-b5">
                            {formatBountyValue(standings?.bountyValue ?? team?.bountyValue ?? 0)}
                          </span>
                        </TeamMenuItem>
                      );
                    })}
                  </div>
                </MenuItems>
              )}
            </AnimatePresence>
          </>
        );
      }}
    </Menu>
  );
};
